import { openDB, IDBPDatabase, IDBPTransaction } from 'idb';
import { LocalUserDataApi, LoginUserDataApi, localUserDef } from './DataApi/UserDataApi';
import { IssuanceBase } from '../../schema/generated/Issuance';
import { LocalLoginUser } from '../../schema/console/loginUser';
import { IssuanceDataApi } from './DataApi/IssuanceDataApi';
import { AsCache, WithOwner } from './IDBSchema';
import { CarryOutBase } from '../../schema/generated/CarryOut';
import { CarryOutDataApi } from './DataApi/CarryOutDataApi';
import { collectEntities } from './DataCollector';
import Time from '../../util/Time';
import { localUserType } from '../../schema/console/user';
import { LotDataApi } from './DataApi/LotDataApi';
import { LotBase } from '../../schema/generated/Lot';
import { ConstructionBase } from '../../schema/generated/Construction';
import { CarryInBase } from '../../schema/generated/CarryIn';
import { ConstructionPlanBase } from '../../schema/generated/ConstructionPlan';
import { ConstructionRecordBase } from '../../schema/generated/ConstructionRecord';
import { GroupBase } from '../../schema/generated/Group';
import { MaterialBase } from '../../schema/generated/Material';
import { ConstructionPlanAreaBase } from '../../schema/generated/ConstructionPlanArea';
import { CarryInDataApi } from './DataApi/CarryInDataApi';
import { ConstructionPlanDataApi } from './DataApi/ConstructionPlanDataApi';
import { ConstructionDataApi } from './DataApi/ConstructionDataApi';
import { ConstructionRecordDataApi } from './DataApi/ConstructionRecordDataApi';
import { ConstructionPlanAreaDataApi } from './DataApi/ConstructionPlanAreaDataApi';
import { GroupDataApi } from './DataApi/GroupDataApi';
import { MaterialDataApi } from './DataApi/MaterialDataApi';
import { EntityDataApi, EntityRowBounds, IdbRowPayload, replaceOwnerRowsInStore } from './IndexedDBApi';
import { idbEntityDefList } from './generated/entityDefs';
import { createGeneratedApiInstances } from './generated/generatedApiInstances';
import type { GeneratedApiInstances } from './generated/generatedApiInstances';

// ストア名と保存する値の対応(ドキュメント用途。実行時の検証はzodスキーマで行う)
export interface IDBInterface {
  Users: {
    value: AsCache<WithOwner<localUserType>>;
  },
  LoginUsers: {
    value: LocalLoginUser;
  },
  Issuances: {
    value: AsCache<WithOwner<IssuanceBase>>;
  },
  CarryOuts: {
    value: AsCache<WithOwner<CarryOutBase>>;
  },
  Lots: {
    value: AsCache<WithOwner<LotBase>>;
  },
  CarryIns: {
    value: AsCache<WithOwner<CarryInBase>>;
  },
  ConstructionPlans: {
    value: AsCache<WithOwner<ConstructionPlanBase>>;
  },
  Constructions: {
    value: AsCache<WithOwner<ConstructionBase>>;
  },
  ConstructionRecords: {
    value: AsCache<WithOwner<ConstructionRecordBase>>;
  },
  ConstructionPlanAreas: {
    value: AsCache<WithOwner<ConstructionPlanAreaBase>>;
  },
  Groups: {
    value: AsCache<WithOwner<GroupBase>>;
  },
  Materials: {
    value: AsCache<WithOwner<MaterialBase>>;
  }
}

export class IDB {
  public loginUserDataApi: LoginUserDataApi;
  public issuanceDataApi: IssuanceDataApi;
  public carryOutDataApi: CarryOutDataApi;
  public userDataApi: LocalUserDataApi;
  public lotDataApi: LotDataApi;
  public carryInDataApi: CarryInDataApi;
  public constructionPlanDataApi: ConstructionPlanDataApi;
  public constructionDataApi: ConstructionDataApi;
  public constructionRecordDataApi: ConstructionRecordDataApi;
  public constructionPlanAreaDataApi: ConstructionPlanAreaDataApi;
  public groupDataApi: GroupDataApi;
  public materialDataApi: MaterialDataApi;

  // 生成された薄いDataApi(マスタ・新規トランザクション系)。
  // entityName(先頭小文字)で各DataApiにアクセスできる。
  public readonly generated: GeneratedApiInstances;

  // SetCache/RemoveInActiveCacheが走査するエンティティAPI一覧
  private readonly entityDataApis: EntityDataApi<EntityRowBounds>[];
  private readonly entityApiByStore: Map<string, EntityDataApi<EntityRowBounds>>;
  // 複数ストアを1トランザクションで扱う処理(replaceOwnerRowsAtomic)用のDB参照。
  private readonly database: IDBPDatabase<IDBInterface>;

  constructor(database: IDBPDatabase<IDBInterface>) {
    this.database = database;
    this.loginUserDataApi = new LoginUserDataApi(database, this.getIDB.bind(this));
    this.issuanceDataApi = new IssuanceDataApi(database, this.getIDB.bind(this));
    this.carryOutDataApi = new CarryOutDataApi(database, this.getIDB.bind(this));
    this.userDataApi = new LocalUserDataApi(database, this.getIDB.bind(this));
    this.lotDataApi = new LotDataApi(database, this.getIDB.bind(this));
    this.carryInDataApi = new CarryInDataApi(database, this.getIDB.bind(this));
    this.constructionPlanDataApi = new ConstructionPlanDataApi(database, this.getIDB.bind(this));
    this.constructionDataApi = new ConstructionDataApi(database, this.getIDB.bind(this));
    this.constructionRecordDataApi = new ConstructionRecordDataApi(database, this.getIDB.bind(this));
    this.constructionPlanAreaDataApi = new ConstructionPlanAreaDataApi(database, this.getIDB.bind(this));
    this.groupDataApi = new GroupDataApi(database, this.getIDB.bind(this));
    this.materialDataApi = new MaterialDataApi(database, this.getIDB.bind(this));

    // 生成された薄いDataApiをインスタンス化する。エンティティ名(先頭小文字)↔クラスの対応は
    // 生成器(tools/generate.ts)が満杯のオブジェクトリテラルとして組み立てる(キャスト不要)。
    this.generated = createGeneratedApiInstances(database, this.getIDB.bind(this));

    const existingEntityApis: EntityDataApi<EntityRowBounds>[] = [
      this.userDataApi,
      this.issuanceDataApi,
      this.carryOutDataApi,
      this.lotDataApi,
      this.carryInDataApi,
      this.constructionPlanDataApi,
      this.constructionDataApi,
      this.constructionRecordDataApi,
      this.constructionPlanAreaDataApi,
      this.groupDataApi,
      this.materialDataApi,
    ];
    const generatedEntityApis: EntityDataApi<EntityRowBounds>[] = Object.values(this.generated);
    this.entityDataApis = [...existingEntityApis, ...generatedEntityApis];
    this.entityApiByStore = new Map(this.entityDataApis.map((api) => [api.def.store, api]));
  }

  private getIDB = (): IDB => this;

  // ストア名からDataApiを取得する(MergeCacheData等、生成された定義を使う処理向け)
  public GetEntityApi(store: string): EntityDataApi<EntityRowBounds> {
    const api = this.entityApiByStore.get(store);
    if (!api) {
      throw new Error(`${store}のDataApiがありません。`);
    }
    return api;
  }

  // 複数ストアの owner 行を1トランザクションで原子的に差し替える。
  // ストアごとに別 tx で書くと途中失敗で半端書込み(IDB↔サーバ乖離)になるため、
  // 多コレクション一括更新(区分の親子複製/カスケード削除/マスタ+細目 等)はこれで一括コミットする。
  // perStore が空なら何もしない(空ストア配列での tx 生成エラーを避ける)。
  public async replaceOwnerRowsAtomic(
    perStore: { store: string; rows: { id: string | number }[] }[],
    ownerId: string,
    cachedAt: number,
  ): Promise<void> {
    if (perStore.length === 0) {
      return;
    }
    const storeNames = perStore.map((entry) => entry.store);
    await this.runAtomicTransaction(storeNames, async (tx) => {
      for (const entry of perStore) {
        await replaceOwnerRowsInStore(tx.objectStore(entry.store), ownerId, entry.rows, cachedAt);
      }
    });
  }

  // 複数ストアへの行を1トランザクションで原子的に put する。
  // replaceOwnerRowsAtomic と異なり「owner の既存行を全削除」はせず、渡された行をそのまま put するのみ。
  // 呼出側が組み立て済みの行(更新後の親・トンボストーン化した子・新規の子 等)を渡す用途向け
  // (例: ConstructionPlanAreaDataApi の子込み put/patch/delete)。
  // perStore が空、または全ストアの rows が空なら何もしない。
  public async putRowsAtomic(perStore: { store: string; rows: IdbRowPayload[] }[]): Promise<void> {
    const targetStores = perStore.filter((entry) => entry.rows.length > 0);
    if (targetStores.length === 0) {
      return;
    }
    const storeNames = targetStores.map((entry) => entry.store);
    await this.runAtomicTransaction(storeNames, async (tx) => {
      for (const entry of targetStores) {
        const store = tx.objectStore(entry.store);
        for (const row of entry.rows) {
          await store.put(row);
        }
      }
    });
  }

  // readwrite トランザクションを開き、work内の処理を実行してコミットする。
  // work が例外を投げた場合は明示的に abort し、途中までの書込みを全ロールバックしてから re-throw する。
  private async runAtomicTransaction(
    storeNames: string[],
    work: (tx: IDBPTransaction<IDBInterface, string[], "readwrite">) => Promise<void>,
  ): Promise<void> {
    const tx = this.database.transaction(storeNames, "readwrite");
    try {
      await work(tx);
      await tx.done;
    } catch (e) {
      // 同期 throw(structuredClone不可 / ストア不在 等)は失敗リクエストが生成されず、
      // トランザクションが非アクティブ化時に自動コミットされてしまう。先行ストアの書込みごと
      // 全ロールバックするため明示的に abort する(自動 abort 済みの場合は無視)。
      try {
        tx.abort();
      } catch {
        // 既に中断/完了済み
      }
      await tx.done.catch(() => {});
      throw e;
    }
  }

  // IndexedDBの初期化
  public static initDB = async () => {
    // (DB名, バージョン, DBのアップデート処理)
    return await openDB<IDBInterface>('TraceabilityLocalDB', 2, {
      upgrade(database, oldVersion, _newVersion, tx) {
        if (!database.objectStoreNames.contains(LoginUserDataApi.dataTable)) {
          database.createObjectStore(LoginUserDataApi.dataTable, { keyPath: "id" });
        }
        const ownerKeyedStores = [localUserDef.store, ...idbEntityDefList.map((def) => def.store)];
        for (const store of ownerKeyedStores) {
          if (!database.objectStoreNames.contains(store)) {
            database.createObjectStore(store, { keyPath: ["id", "ownerId"] });
          }
        }
        // v2: 外部キー検索用のセカンダリインデックスを追加(既存レコードは保持される)
        if (oldVersion < 2) {
          for (const def of idbEntityDefList) {
            const indexedStore = tx.objectStore(def.store);
            for (const index of def.indexes) {
              if (!indexedStore.indexNames.contains(index.name)) {
                indexedStore.createIndex(index.name, index.keyPath);
              }
            }
          }
        }
      }
    });
  };

  // APIレスポンスに含まれる全エンティティを収集してローカルDBへキャッシュする
  public async SetCache(data: unknown, ownerId: string, topKeyName: string) {
    const owner = { ownerId };
    const cachedAt = Time.Now().GetTime();
    const dataWithTopKeyName = { [topKeyName]: data };

    for (const api of this.entityDataApis) {
      const entities = collectEntities(dataWithTopKeyName, api.def.collectKeys, api.def.schema);
      await api.cacheAll(entities, owner, cachedAt);
    }
  }

  // ログイン中でないユーザーのキャッシュを全ストアから削除する
  public async RemoveInActiveCache() {
    try {
      const activeUsers = await this.loginUserDataApi.getAll();
      const activeUserIds = { ids: activeUsers.map((user) => user.id) };
      for (const api of this.entityDataApis) {
        await api.removeInActiveCaches(activeUserIds);
      }
    } catch {
      console.log("データ削除に失敗しました。");
    }
  }
}
